// Photos & Media (Phase 3 Module 5 Slice A) — parent + teacher helpers.

import { supabase, isSupabaseConfigured } from "./supabase";
import { BUCKETS } from "./storage";
import {
  transformedSignedUrl,
  responsiveImage,
  prepareImageForUpload,
} from "./imageOptimization";

// Extend BUCKETS in place so callers using `BUCKETS.childPhotos` resolve.
// (storage.js already spreads these; this file only adds the new slot.)
if (!BUCKETS.childPhotos) {
  BUCKETS.childPhotos = "child-photos";
}

function assertLive() {
  if (!isSupabaseConfigured) throw new Error("Photos module requires live Supabase.");
}

const PHOTO_COLS =
  "id, child_id, organization_id, classroom_id, uploaded_by, storage_path, caption, taken_at, created_at, moderation_status";

const SIGNED_URL_TTL = 60 * 60; // 1 hour

// ---------------------------------------------------------------------------
// Listing
// ---------------------------------------------------------------------------

/** List photos for a single child (parent + teacher + admin callers). */
export async function listChildPhotos(childId, { limit = 100 } = {}) {
  assertLive();
  const { data, error } = await supabase
    .from("child_photos")
    .select(PHOTO_COLS)
    .eq("child_id", childId)
    .order("taken_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

/**
 * Bulk-fetch photos for every child in a family (parent dashboard / activity).
 * Uses a single IN query so RLS filters once.
 */
export async function listPhotosForChildren(childIds, { limit = 100 } = {}) {
  assertLive();
  if (!childIds || childIds.length === 0) return [];
  const { data, error } = await supabase
    .from("child_photos")
    .select(PHOTO_COLS)
    .in("child_id", childIds)
    .order("taken_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

// ---------------------------------------------------------------------------
// Teacher upload / delete
// ---------------------------------------------------------------------------

const MAX_PHOTO_BYTES = 8 * 1024 * 1024; // 8 MB — phone photos are well under

export async function uploadChildPhoto({
  child,        // full child row — we need classroom_id + organization_id
  file,
  uploadedBy,
  caption,
  takenAt,
}) {
  assertLive();
  if (!child?.id || !child?.organization_id) {
    throw new Error("Missing child metadata");
  }
  // Best-effort downscale before the bytes leave the device. Keeps phone
  // shots under ~400 KB instead of multi-megabyte JPEGs — saves bandwidth
  // for the teacher uploading and for every parent fetching it later.
  const optimised = await prepareImageForUpload(file).catch(() => file);
  const useFile = optimised || file;
  if (useFile.size > MAX_PHOTO_BYTES) {
    throw new Error("Photo too large — limit is 8 MB.");
  }
  const path = buildPath(child.id, useFile.name);

  const { error: upErr } = await supabase.storage
    .from("child-photos")
    .upload(path, useFile, {
      cacheControl: "3600",
      upsert: false,
      contentType: useFile.type || "image/jpeg",
    });
  if (upErr) throw upErr;

  const { data, error } = await supabase
    .from("child_photos")
    .insert({
      child_id: child.id,
      organization_id: child.organization_id,
      classroom_id: child.classroom_id || null,
      uploaded_by: uploadedBy || null,
      storage_path: path,
      caption: caption?.trim() || null,
      taken_at: takenAt || new Date().toISOString(),
    })
    .select(PHOTO_COLS)
    .single();
  if (error) {
    // best-effort cleanup to avoid orphan objects
    await supabase.storage.from("child-photos").remove([path]).catch(() => {});
    throw error;
  }
  return data;
}

export async function deleteChildPhoto({ id, storagePath }) {
  assertLive();
  const { error } = await supabase.from("child_photos").delete().eq("id", id);
  if (error) throw error;
  if (storagePath) {
    await supabase.storage
      .from("child-photos")
      .remove([storagePath])
      .catch(() => {});
  }
}

// ---------------------------------------------------------------------------
// Signed URLs (cached per-path for the TTL so lightbox re-opens are instant)
// ---------------------------------------------------------------------------

/**
 * Resolve a signed URL for a child photo. Pass `opts` to request a
 * server-side transform (width / quality / resize); omit for the
 * original bytes. Cached per path+opts in the shared image cache.
 */
export async function photoSignedUrl(path, opts) {
  assertLive();
  return transformedSignedUrl("child-photos", path, opts, { ttl: SIGNED_URL_TTL });
}

/**
 * Build {src, srcSet, sizes} for a responsive child-photo <img>.
 * Always returns null when the path is missing.
 */
export async function photoResponsive(path, opts) {
  return responsiveImage("child-photos", path, opts);
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function buildPath(childId, name) {
  const ext = (name || "").split(".").pop()?.toLowerCase() || "jpg";
  const rand = Math.random().toString(36).slice(2, 10);
  return `${childId}/${Date.now()}-${rand}.${ext}`;
}

export function formatPhotoDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}
